import { useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import xhr from 'core/utils/xhr';
import fixture_request, {shouldUseFixtureFor} from 'fixtures/fixture-server';
import ErrorContext from 'ErrorContext';
import { getLocalAccessToken } from "hooks/useAuthTokens";

const memoizeKeygen = (name, params) => name + JSON.stringify(params);
const memoizedResponses = {};
const defaultMemoizeTime = 10 * 60 * 1000; // 10 min

const remember = (key, val)=> {
  memoizedResponses[key] = val;
  setTimeout(()=> memoizedResponses[key] = undefined, defaultMemoizeTime);
};

let lastTimestamp = null;

export default function useQuery(appState){
  const errors = useContext(ErrorContext)

  function query(name, params={}, xhrConfig={}, options={defaultErrorHandling: true}) {
    let accessToken = getLocalAccessToken();
    let request_id = uuidv4();

    let now = Date.now();
    if (lastTimestamp) {
      let diff = now - lastTimestamp;
      if(diff < 60000) {
        return Promise.resolve({}); 
      }
    }

    /* Memoization handling */
    const memoize = xhrConfig.memoize || false;
    const memoizeKey = memoize ? memoizeKeygen(name, params) : undefined;
    delete xhrConfig.memoize;
    if(memoize && memoizedResponses[memoizeKey]){
      return Promise.resolve(memoizedResponses[memoizeKey]);
    }

    /* Fixture handling */
    if(shouldUseFixtureFor(name)){
      return fixture_request.query(name, params);
    }

    let baseUrl = xhrConfig.base_url || `${window.api_url}/query`;
    let url = `${baseUrl}/${name}`;

    /* Normal XHR handling */
    const headers = xhrConfig.headers || {};
    if(accessToken){
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    if(appState && appState.activeCompany){
      headers['X-Active-Company'] = `${appState.activeCompany.id}`;
    }
    headers['X-Active-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    headers['X-Request-Id'] = request_id;

    // Helper to build a consistent, enriched error object
    const buildError = (message, response, statusCode) => {
      const error = new Error(message);
      error.response = response;
      error.url = url;
      error.statusCode = statusCode;
      error.x_request_id = request_id;
      return error;
    };

    // If there was no response after a set time, return a timeout error
    const timeoutId = setTimeout(() => {
      errors.addMessage("The api call timeout", "", url, "", request_id);
      lastTimestamp = now;
    }, window.api_wait_time || 30000);

    return xhr({
      method: xhrConfig.method || 'GET',
      url: url,
      data: params,
      headers
    }, xhrConfig).then(
      function(response = {}, statusCode = 200){
        if (response.success) {
          response = response.payload || response;
          if(memoize) remember(memoizeKey, response);
          clearTimeout(timeoutId);
          return response;
        }
        else if (response && response.payload) {
          errors.addMessage("Unexpected Application Error", response.payload, url, statusCode, request_id);
          clearTimeout(timeoutId);
          return Promise.reject(
            buildError(response.payload.error || "Request failed", response, statusCode)
          );
        }
        else {
          clearTimeout(timeoutId);
          return response || {};
        }
      },
      function([statusCode, response]) {
        errors.addMessage("Unexpected Network Error", response, url, statusCode, request_id);
        clearTimeout(timeoutId);
        return Promise.resolve({});
      });
  }

  return query
}